import React, { useState, useEffect, useCallback } from 'react';
import { Select, Input, message } from 'antd';
import { apiGetAllBasicCodeFuncs, apiGetBasicCode, apiAddBasicCode, apiUpdateBasicCode, apiDeleteBasicCode } from '../../api';

const { Option } = Select;

const funcDescriptions = {
    ACC: 'Đơn vị lập tài khoản', BB: 'Lương cơ bản', BADS: 'Phương thức xử lý', CDT: 'Loại tài liệu',
    COUNTRY: 'Quốc tịch', CV: 'Chức vụ', DD: 'Hiệu suất', EMP: 'Nhân viên',
    EMPTYPE: 'Loại nhân viên', GOODS: 'Phương thức khen thưởng', GROUPID: 'Bộ phận',
    GRP: 'Quyền hạn', GRPS: 'Phân loại giờ làm thêm', JB: 'Loại nghỉ phép', LEV: 'Chức vụ',
    QC: 'Chuyên cần', SHIFT: 'Ca làm việc', TAX: 'Thuế', TT: 'TT', UNIT: 'Đơn vị',
    WB: 'Đơn vị bên ngoài', WHSNO: 'Phân loại xưởng', XIP: 'WSIP', ZUNO: 'Nhóm'
};

const initialFormData = {
    func: '',
    SYS_TYPE: '',
    SYS_VALUE: ''
};

const BasicCodeManagement = () => {
    // --- State Management ---
    const [funcs, setFuncs] = useState([]);
    const [codes, setCodes] = useState([]);
    const [selectedFunc, setSelectedFunc] = useState(null);
    const [loadingFuncs, setLoadingFuncs] = useState(false);
    const [loadingCodes, setLoadingCodes] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [formData, setFormData] = useState(initialFormData);

    // --- Data Fetching ---
    const fetchFuncs = useCallback(async () => {
        setLoadingFuncs(true);
        try {
            const funcsData = await apiGetAllBasicCodeFuncs();
            setFuncs(funcsData.map(f => f.FUNC).sort());
        } catch (error) {
            message.error(`Không thể tải danh sách loại mã: ${error.message}`);
        } finally {
            setLoadingFuncs(false);
        }
    }, []);

    useEffect(() => {
        fetchFuncs();
    }, [fetchFuncs]);

    const fetchCodesByFunc = useCallback(async (func) => {
        if (!func) {
            setCodes([]);
            return;
        }
        setLoadingCodes(true);
        try {
            const codesData = await apiGetBasicCode(func);
            setCodes(codesData);
        } catch (error) {
            message.error(`Không thể tải dữ liệu cho loại mã ${func}: ${error.message}`);
        } finally {
            setLoadingCodes(false);
        }
    }, []);

    // --- Handlers ---
    const handleFuncChange = (func) => {
        setSelectedFunc(func);
        fetchCodesByFunc(func);
    };
    
    const handleFormChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleAdd = () => {
        setEditingRecord(null);
        const newFormData = selectedFunc ? { ...initialFormData, func: selectedFunc } : initialFormData;
        setFormData(newFormData);
        setIsFormVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setFormData({
            ...record,
            func: selectedFunc,
        });
        setIsFormVisible(true);
    };
    
    const handleCancel = () => {
        setIsFormVisible(false);
        setEditingRecord(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Bạn có chắc chắn muốn xóa mã này?")) return;
        try {
            await apiDeleteBasicCode(id);
            message.success('Xóa mã thành công');
            fetchCodesByFunc(selectedFunc);
        } catch (error) {
            message.error(`Lỗi khi xóa: ${error.message}`);
        }
    };

    const handleSave = async () => {
        if (!formData.func || !formData.SYS_TYPE || !formData.SYS_VALUE) {
            message.error('Vui lòng điền đầy đủ các trường thông tin.');
            return;
        }
        setIsSaving(true);
        try {
            if (editingRecord) {
                await apiUpdateBasicCode(editingRecord.SYS_ID, {
                    SYS_TYPE: formData.SYS_TYPE,
                    SYS_VALUE: formData.SYS_VALUE,
                });
                message.success('Cập nhật mã thành công');
            } else {
                await apiAddBasicCode({
                    func: formData.func,
                    SYS_TYPE: formData.SYS_TYPE,
                    SYS_VALUE: formData.SYS_VALUE,
                });
                message.success('Thêm mã mới thành công');
            }
            setIsFormVisible(false);
            
            if(formData.func === selectedFunc) {
                fetchCodesByFunc(formData.func);
            }
            if (!funcs.includes(formData.func)) {
                fetchFuncs();
            }
        } catch (error) {
            message.error(`Lưu thất bại: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    const tableHeaders = [
        { key: 'SYS_TYPE', label: 'Mã' },
        { key: 'SYS_VALUE', label: 'Giá trị' },
        { key: 'action', label: 'Hành động', width: '150px' }
    ];

    return (
        <div className="p-4" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">QUẢN LÝ MÃ CƠ BẢN</h2>

            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                <div>
                     <label className="block text-sm font-medium text-gray-700">Chọn loại mã</label>
                    <Select
                        showSearch
                        style={{ width: 350 }}
                        className="mt-1"
                        placeholder="Chọn hoặc tìm loại mã để quản lý"
                        loading={loadingFuncs}
                        onChange={handleFuncChange}
                        value={selectedFunc}
                        filterOption={(input, option) =>
                            (option.children.toLowerCase() || '').includes(input.toLowerCase())
                        }
                        disabled={funcs.length === 0}
                    >
                        {funcs.map(func => (
                            <Option key={func} value={func}>
                                {`${func} (${funcDescriptions[func] || 'Chưa có mô tả'})`}
                            </Option>
                        ))}
                    </Select>
                </div>
                <button onClick={handleAdd} className="px-6 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700">
                    Thêm mới
                </button>
            </div>

            {isFormVisible && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-blue-200">
                     <h3 className="text-lg font-semibold text-gray-800 mb-4">{editingRecord ? 'Sửa Mã' : 'Thêm Mã Mới'}</h3>
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Loại mã *</label>
                            <Select
                                showSearch
                                placeholder="Chọn hoặc tìm loại mã"
                                value={formData.func}
                                onChange={(v) => handleFormChange('func', v)}
                                disabled={!!editingRecord}
                                style={{ width: '100%' }}
                                className="mt-1"
                            >
                                {funcs.map(func => (
                                    <Option key={func} value={func}>
                                        {`${func} (${funcDescriptions[func] || 'Chưa có mô tả'})`}
                                    </Option>
                                ))}
                            </Select>
                         </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Mã *</label>
                            <Input value={formData.SYS_TYPE} onChange={(e) => handleFormChange('SYS_TYPE', e.target.value)} className="mt-1" placeholder="Ví dụ: KD, HCNS..." />
                         </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Giá trị *</label>
                            <Input value={formData.SYS_VALUE} onChange={(e) => handleFormChange('SYS_VALUE', e.target.value)} className="mt-1" placeholder="Ví dụ: Phòng Kinh Doanh..." />
                         </div>
                     </div>
                     <div className="flex justify-end gap-4 mt-4">
                        <button onClick={handleCancel} className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                            Hủy
                        </button>
                        <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                            {isSaving ? 'Đang lưu...' : 'Lưu'}
                        </button>
                    </div>
                </div>
            )}

            <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '70vh' }}>
                <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100">
                        <tr>
                            {tableHeaders.map(header => (
                                <th key={header.key} className="p-2 font-semibold text-gray-600 whitespace-nowrap border-b border-r text-left" style={{width: header.width}}>
                                    {header.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                     <tbody className="divide-y divide-gray-200">
                        {loadingCodes ? (
                             <tr><td colSpan={tableHeaders.length} className="p-4 text-center">Đang tải dữ liệu...</td></tr>
                        ) : codes.length === 0 ? (
                             <tr><td colSpan={tableHeaders.length} className="p-4 text-center text-gray-500">{selectedFunc ? 'Không có dữ liệu cho loại mã này.' : 'Vui lòng chọn một loại mã để xem dữ liệu.'}</td></tr>
                        ) : codes.map(row => (
                            <tr key={row.SYS_ID} className="hover:bg-gray-50">
                                {tableHeaders.map(header => (
                                    <td key={`${row.SYS_ID}-${header.key}`} className="p-2 whitespace-nowrap border-r">
                                        {header.key === 'action' ? (
                                             <div className="flex space-x-2">
                                                <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline">Sửa</button>
                                                <button onClick={() => handleDelete(row.SYS_ID)} className="text-red-600 hover:underline">Xóa</button>
                                            </div>
                                        ) : (
                                            row[header.key]
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                     </tbody>
                </table>
            </div>
        </div>
    );
};

export default BasicCodeManagement;
